import type { EducationDetailDto } from "../types/client-education-detail.types";

/**
 * Talep toplama penceresinin bugün açık olup olmadığını belirler.
 *
 * Açık sayılması için:
 *   1. `isAcceptingRequestCollection === true` olmalı.
 *   2. En az bir tarafı (başlangıç veya bitiş) tanımlı bir pencere olmalı.
 *   3. Bugün (yerel gün bazında) pencereye dahil olmalı; null uçlar açık taraf sayılır.
 *
 * `now` parametresi test edilebilirlik için override edilebilir.
 */
export function isRequestCollectionOpen(
  education: Pick<
    EducationDetailDto,
    | "isAcceptingRequestCollection"
    | "requestCollectionStartDate"
    | "requestCollectionEndDate"
  >,
  now: Date = new Date(),
): boolean {
  if (education.isAcceptingRequestCollection !== true) return false;

  const toMidnight = (v?: string | null): number | null => {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };

  const todayMs = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const startMs = toMidnight(education.requestCollectionStartDate);
  const endMs = toMidnight(education.requestCollectionEndDate);

  if (startMs == null && endMs == null) return false;
  return (
    (startMs == null || todayMs >= startMs) &&
    (endMs == null || todayMs <= endMs)
  );
}

/**
 * Talep toplamanın gelecekte bir tarihte açılacak olup olmadığını döner.
 * UI'da "{date} tarihinde açılacak" mesajı için kullanılır.
 */
export function requestCollectionOpensAt(
  education: Pick<
    EducationDetailDto,
    "isAcceptingRequestCollection" | "requestCollectionStartDate"
  >,
  now: Date = new Date(),
): Date | null {
  if (education.isAcceptingRequestCollection !== true) return null;
  if (!education.requestCollectionStartDate) return null;
  const start = new Date(education.requestCollectionStartDate);
  if (Number.isNaN(start.getTime())) return null;
  return start.getTime() > now.getTime() ? start : null;
}
